import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { useModuleStatus } from '@/hooks/useModuleStatus.ts'
import { BetaBadge } from '@/components/ui/BetaBadge.tsx'
import {
  Palmtree,
  Plus,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  User,
  AlertTriangle,
  FileText,
  Upload,
} from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  PageHeader,
  Select,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@/components/ui'
import type { BadgeVariant } from '@/styles/tokens.ts'

type VacationType = 'vacation' | 'sick' | 'special' | 'overtime'
type VacationStatus = 'pending' | 'approved' | 'rejected'

interface VacationRequest {
  id: string
  userId: string
  userName: string
  type: VacationType
  startDate: string
  endDate: string
  days: number
  status: VacationStatus
  reason?: string
  approverName?: string
  createdAt: string
}

interface VacationBalance {
  total: number
  used: number
  planned: number
  remaining: number
  sickDays: number
}

type SubPage = 'requests' | 'team' | 'balance'

const TYPE_META: Record<
  VacationType,
  { label: string; variant: BadgeVariant; icon: typeof Palmtree }
> = {
  vacation: { label: 'Urlaub', variant: 'info', icon: Palmtree },
  sick: { label: 'Krank', variant: 'danger', icon: AlertTriangle },
  special: { label: 'Sonderurlaub', variant: 'brand', icon: FileText },
  overtime: { label: 'Überstundenabbau', variant: 'warning', icon: Clock },
}

const STATUS_VARIANT_OVERRIDE: Record<VacationStatus, BadgeVariant> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
}

const STATUS_LABEL_OVERRIDE: Record<VacationStatus, string> = {
  pending: 'Ausstehend',
  approved: 'Genehmigt',
  rejected: 'Abgelehnt',
}

function TypeBadge({ type }: { type: VacationType }) {
  const meta = TYPE_META[type]
  const Icon = meta.icon
  return (
    <Badge variant={meta.variant} size="sm">
      <Icon className="w-3 h-3" />
      {meta.label}
    </Badge>
  )
}

function VacationStatusBadge({ status }: { status: VacationStatus }) {
  return (
    <StatusBadge
      status={status}
      variant={STATUS_VARIANT_OVERRIDE[status]}
      label={STATUS_LABEL_OVERRIDE[status]}
    />
  )
}

export function UrlaubPage() {
  const { isBeta } = useModuleStatus('urlaub')
  const [subPage, setSubPage] = useState<SubPage>('requests')
  const [showNew, setShowNew] = useState(false)

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <Palmtree className="w-7 h-7 text-brand-500" />
            Urlaubsverwaltung
            {isBeta && <BetaBadge size="md" />}
          </span>
        }
        description="Urlaubsanträge, Genehmigung und Übersicht"
        actions={
          <Button
            variant="primary"
            size="md"
            leadingIcon={<Plus className="w-4 h-4" />}
            onClick={() => setShowNew((v) => !v)}
          >
            Neuer Antrag
          </Button>
        }
      />

      {showNew && <NewRequestForm onClose={() => setShowNew(false)} />}

      <Tabs value={subPage} onValueChange={(v) => setSubPage(v as SubPage)}>
        <TabsList>
          <TabsTrigger value="requests">
            <FileText className="w-4 h-4" />
            Meine Anträge
          </TabsTrigger>
          <TabsTrigger value="team">
            <User className="w-4 h-4" />
            Team-Übersicht
          </TabsTrigger>
          <TabsTrigger value="balance">
            <Calendar className="w-4 h-4" />
            Urlaubskonto
          </TabsTrigger>
        </TabsList>
        <TabsContent value="requests">
          <RequestsSection />
        </TabsContent>
        <TabsContent value="team">
          <TeamSection />
        </TabsContent>
        <TabsContent value="balance">
          <BalanceSection />
        </TabsContent>
      </Tabs>
    </div>
  )
}

function NewRequestForm({ onClose }: { onClose: () => void }) {
  const [type, setType] = useState<VacationType>('vacation')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [reason, setReason] = useState('')
  const { toast } = useToast()

  return (
    <Card>
      <Card.Header>
        <Card.Title>Neuen Antrag stellen</Card.Title>
      </Card.Header>
      <Card.Body className="space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Select<VacationType>
            label="Art"
            value={type}
            onValueChange={setType}
            options={[
              { value: 'vacation', label: 'Urlaub' },
              { value: 'sick', label: 'Krankmeldung' },
              { value: 'special', label: 'Sonderurlaub' },
              { value: 'overtime', label: 'Überstundenabbau' },
            ]}
          />
          <Input
            label="Von"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Input
            label="Bis"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <Input
            label="Grund (optional)"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="z.B. Familienfeier"
          />
        </div>
        {type === 'sick' && (
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<Upload className="w-4 h-4" />}
            className="border-dashed"
          >
            AU-Bescheinigung hochladen (PDF oder Foto)
          </Button>
        )}
      </Card.Body>
      <Card.Footer>
        <Button variant="ghost" size="md" onClick={onClose}>
          Abbrechen
        </Button>
        <Button
          variant="primary"
          size="md"
          onClick={() => toast({ title: 'Antrag eingereicht', tone: 'success' })}
        >
          Antrag einreichen
        </Button>
      </Card.Footer>
    </Card>
  )
}

function RequestsSection() {
  const { data: requests = [] } = useQuery<VacationRequest[]>({
    queryKey: ['vacation-requests'],
    queryFn: () => apiClient.get('/vacation/requests').then((r) => r.data),
    retry: false,
  })

  if (requests.length === 0) {
    return (
      <Card>
        <Card.Body>
          <EmptyState
            title="Keine Anträge vorhanden"
            description="Stelle einen neuen Antrag über den Button oben rechts."
            icon={<FileText />}
          />
        </Card.Body>
      </Card>
    )
  }

  return (
    <Card>
      <div className="divide-y divide-gray-100">
        {requests.map((req) => {
          const meta = TYPE_META[req.type]
          const TypeIcon = meta.icon
          return (
            <div key={req.id} className="flex items-center gap-4 px-4 py-3">
              <TypeIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <TypeBadge type={req.type} />
                  <p className="text-body font-medium text-gray-900">
                    {new Date(req.startDate).toLocaleDateString('de-DE')} –{' '}
                    {new Date(req.endDate).toLocaleDateString('de-DE')}
                  </p>
                  <span className="text-caption text-gray-500">({req.days} Tage)</span>
                </div>
                {req.reason && (
                  <p className="text-caption text-gray-500 mt-0.5">{req.reason}</p>
                )}
              </div>
              <VacationStatusBadge status={req.status} />
            </div>
          )
        })}
      </div>
    </Card>
  )
}

function TeamSection() {
  const { data: teamRequests = [] } = useQuery<VacationRequest[]>({
    queryKey: ['team-vacation'],
    queryFn: () => apiClient.get('/vacation/team').then((r) => r.data),
    retry: false,
  })

  const queryClient = useQueryClient()
  const { toast } = useToast()
  const approve = useMutation({
    mutationFn: (id: string) =>
      apiClient.patch(`/vacation/requests/${id}`, { status: 'approved' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-vacation'] })
      toast({ title: 'Antrag genehmigt', tone: 'success' })
    },
    onError: () => toast({ title: 'Genehmigung fehlgeschlagen', tone: 'danger' }),
  })
  const reject = useMutation({
    mutationFn: (id: string) =>
      apiClient.patch(`/vacation/requests/${id}`, { status: 'rejected' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-vacation'] })
      toast({ title: 'Antrag abgelehnt', tone: 'neutral' })
    },
    onError: () => toast({ title: 'Ablehnung fehlgeschlagen', tone: 'danger' }),
  })

  const pending = teamRequests.filter((r) => r.status === 'pending')
  const decided = teamRequests.filter((r) => r.status !== 'pending')

  return (
    <div className="space-y-6">
      {pending.length > 0 && (
        <Card className="border-amber-200">
          <Card.Header className="bg-amber-50 border-amber-200">
            <Card.Title className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-500" />
              Offene Anträge ({pending.length})
            </Card.Title>
          </Card.Header>
          <div className="divide-y divide-amber-100">
            {pending.map((req) => (
              <div key={req.id} className="flex items-center gap-4 px-4 py-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-body font-medium text-gray-900">{req.userName}</p>
                    <TypeBadge type={req.type} />
                  </div>
                  <p className="text-caption text-gray-600">
                    {new Date(req.startDate).toLocaleDateString('de-DE')} –{' '}
                    {new Date(req.endDate).toLocaleDateString('de-DE')} ({req.days} Tage)
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    leadingIcon={<CheckCircle className="w-3.5 h-3.5" />}
                    onClick={() => approve.mutate(req.id)}
                    loading={approve.isPending && approve.variables === req.id}
                  >
                    Genehmigen
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    leadingIcon={<XCircle className="w-3.5 h-3.5" />}
                    onClick={() => reject.mutate(req.id)}
                    loading={reject.isPending && reject.variables === req.id}
                  >
                    Ablehnen
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div>
        <h3 className="text-body font-semibold text-gray-900 mb-3">Entschiedene Anträge</h3>
        {decided.length === 0 ? (
          <Card>
            <Card.Body>
              <EmptyState title="Keine Einträge" size="sm" />
            </Card.Body>
          </Card>
        ) : (
          <Card>
            <div className="divide-y divide-gray-100">
              {decided.map((req) => (
                <div key={req.id} className="flex items-center gap-4 px-4 py-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-body font-medium text-gray-900">{req.userName}</p>
                      <TypeBadge type={req.type} />
                    </div>
                    <p className="text-caption text-gray-500">
                      {new Date(req.startDate).toLocaleDateString('de-DE')} –{' '}
                      {new Date(req.endDate).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                  <VacationStatusBadge status={req.status} />
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}

function BalanceSection() {
  const { data: balance } = useQuery<VacationBalance>({
    queryKey: ['vacation-balance'],
    queryFn: () => apiClient.get('/vacation/balance').then((r) => r.data),
    retry: false,
  })

  const b = balance ?? { total: 30, used: 12, planned: 5, remaining: 13, sickDays: 3 }

  const stats: { label: string; value: number; unit: string; color: string }[] = [
    { label: 'Jahresanspruch', value: b.total, unit: 'Tage', color: 'text-gray-900' },
    { label: 'Genommen', value: b.used, unit: 'Tage', color: 'text-blue-600' },
    { label: 'Geplant', value: b.planned, unit: 'Tage', color: 'text-amber-600' },
    { label: 'Verbleibend', value: b.remaining, unit: 'Tage', color: 'text-green-600' },
    { label: 'Krankheitstage', value: b.sickDays, unit: 'Tage', color: 'text-red-600' },
  ]

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {stats.map((stat) => (
          <Card key={stat.label}>
            <Card.Body>
              <p className="text-caption text-gray-500">{stat.label}</p>
              <p className={cn('text-h2', stat.color)}>{stat.value}</p>
              <p className="text-caption text-gray-400">{stat.unit}</p>
            </Card.Body>
          </Card>
        ))}
      </div>

      <Card>
        <Card.Header>
          <Card.Title>Urlaubsverbrauch 2026</Card.Title>
        </Card.Header>
        <Card.Body className="space-y-2">
          <div className="h-4 bg-gray-100 rounded-full overflow-hidden flex">
            <div
              className="bg-blue-500 h-full"
              style={{ width: `${(b.used / b.total) * 100}%` }}
              title={`Genommen: ${b.used}`}
            />
            <div
              className="bg-amber-400 h-full"
              style={{ width: `${(b.planned / b.total) * 100}%` }}
              title={`Geplant: ${b.planned}`}
            />
          </div>
          <div className="flex gap-4 text-caption text-gray-500">
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
              Genommen ({b.used})
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
              Geplant ({b.planned})
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-gray-200" />
              Verfügbar ({b.remaining})
            </span>
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}
